import { useState, useEffect } from 'react';
import { Package, Plus, Trash2, Check, ChevronLeft, ArrowRight } from 'lucide-react';
import { AnimateIn } from '../../shared/ui/AnimateIn';
import { Button } from '../../shared/ui/Button';
import { Modal } from '../../shared/ui/Modal';
import { ConfirmModal } from '../../shared/ui/ConfirmModal';
import { Skeleton } from '../../shared/ui/Skeleton';
import { EmptyState } from '../../shared/ui/EmptyState';
import { ErrorState } from '../../shared/ui/ErrorState';
import { useToast } from '../../shared/ui/Toast';
import {
  useSavedEquipment,
  useAddSavedEquipment,
  useDeleteSavedEquipment,
} from '../../features/saved-equipment/hooks';
import { PhotoUpload } from '../../features/requests/components/PhotoUpload';
import { uploadEquipmentPhoto } from '../../shared/api/cloudinary';
import { usePageMeta } from '../../shared/hooks/usePageMeta';
import type { EquipmentKind, WeightClass } from '../../shared/types/app';

const EQUIPMENT_KINDS: EquipmentKind[] = [
  'Excavator', 'Backhoe', 'Front-end Loader', 'Roller',
  'Hydraulic Hammer', 'Lowboy Trailer', 'Tractor Head', 'Manlift', 'Custom',
];

const WEIGHT_CLASSES: { value: WeightClass; label: string; range: string }[] = [
  { value: 'light',       label: 'Light',       range: '< 5t'   },
  { value: 'medium',      label: 'Medium',      range: '5–15t'  },
  { value: 'heavy',       label: 'Heavy',       range: '15–30t' },
  { value: 'super_heavy', label: 'Super Heavy', range: '30t+'   },
];

type StepName = 'Type' | 'Weight' | 'Details';
const STEPS: StepName[] = ['Type', 'Weight', 'Details'];

const labelClass = 'block text-[11px] font-bold text-muted uppercase tracking-widest mb-2';
const inputClass = 'w-full rounded-xl bg-bg px-3 py-2.5 text-sm text-text placeholder-muted outline-none transition-colors focus-visible:ring-1 focus-visible:ring-accent';

type DraftState = {
  label: string;
  equipmentKind: EquipmentKind | undefined;
  equipmentDesc: string;
  weightClass: WeightClass | undefined;
  make: string;
  model: string;
  estimatedWeight: string;
  photoUrl: string | null;
};

const emptyDraft: DraftState = {
  label: '',
  equipmentKind: undefined,
  equipmentDesc: '',
  weightClass: undefined,
  make: '',
  model: '',
  estimatedWeight: '',
  photoUrl: null,
};

export function SavedEquipmentPage() {
  usePageMeta('Saved Equipment');
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [step, setStep] = useState(0);

  const { data: items = [], isLoading, isError, refetch } = useSavedEquipment();
  const addEquipment = useAddSavedEquipment();
  const deleteEquipment = useDeleteSavedEquipment();
  const { toast } = useToast();

  const stepName = STEPS[step];
  const isLastStep = step === STEPS.length - 1;

  const closeModal = () => { setAddOpen(false); setDraft(emptyDraft); setStep(0); };

  // Reset to the first step each time the modal is opened fresh, so a previous
  // in-progress draft never reopens deep into the flow.
  useEffect(() => {
    if (addOpen) setStep(0);
  }, [addOpen]);

  function canProceed(): boolean {
    switch (stepName) {
      case 'Type':
        return !!draft.equipmentKind;
      case 'Weight':
        return !!draft.weightClass;
      case 'Details':
        return !!draft.label.trim() && !!draft.make.trim() && !!draft.model.trim();
    }
  }

  // Continue disables silently on an invalid step — this flips true the moment someone tries to
  // advance anyway, so the relevant field(s) below can show what's missing.
  const [stepAttempted, setStepAttempted] = useState(false);
  useEffect(() => { setStepAttempted(false); }, [step]);

  const next = () => {
    if (canProceed()) setStep((s) => Math.min(STEPS.length - 1, s + 1));
    else setStepAttempted(true);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const canSave = !!draft.label.trim() && !!draft.equipmentKind && !!draft.weightClass
    && !!draft.make.trim() && !!draft.model.trim();

  const handleAdd = async () => {
    if (!canSave || !draft.equipmentKind || !draft.weightClass) {
      setStepAttempted(true);
      return;
    }
    try {
      await addEquipment.mutateAsync({
        label: draft.label.trim(),
        equipmentKind: draft.equipmentKind,
        equipmentDesc: draft.equipmentDesc,
        weightClass: draft.weightClass,
        make: draft.make.trim(),
        model: draft.model.trim(),
        estimatedWeight: draft.estimatedWeight ? Number(draft.estimatedWeight) : undefined,
        photoUrl: draft.photoUrl,
      });
      toast('Equipment saved!', 'success');
      closeModal();
    } catch (err) {
      toast((err as Error).message || 'Failed to save equipment', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEquipment.mutateAsync(deleteTarget);
      toast('Equipment removed.', 'info');
      setDeleteTarget(null);
    } catch (err) {
      toast((err as Error).message || 'Failed to remove equipment', 'error');
    }
  };

  return (
    <div className="flex flex-col max-w-2xl mx-auto w-full">

      {/* Header */}
      <div className="pb-5 flex items-end justify-between">
        <div>
          <p className="text-xs font-bold text-muted uppercase tracking-widest mb-1">Customer</p>
          <h1 className="text-2xl font-black text-text">Saved Equipment</h1>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setAddOpen(true)}
          className="!text-accent normal-case !tracking-normal !border-2 !border-accent/30 rounded-xl hover:!bg-accent hover:!text-black"
        >
          <Plus size={13} />
          Add Equipment
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="rounded-xl overflow-hidden bg-surface-raised/40">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-start gap-4 px-4 py-4">
              <Skeleton className="h-8 w-8 rounded-xl flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="py-12">
          <ErrorState onRetry={() => refetch()} />
        </div>
      ) : items.length === 0 ? (
        <div className="py-20">
          <EmptyState
            title="No saved equipment"
            description="Save equipment you book often to speed up future requests."
            icon={<Package size={24} />}
          />
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden bg-surface-raised/40">
          {items.map((item, i) => (
            <AnimateIn key={item.id} animation="fade-up" delay={i * 60} className="w-full">
            <div className="flex items-start gap-4 px-4 py-4 hover:bg-surface-raised transition-colors">
              {item.photoUrl ? (
                <img
                  src={item.photoUrl}
                  alt={item.label}
                  className="h-8 w-8 rounded-xl object-cover flex-shrink-0 mt-0.5"
                />
              ) : (
                <div className="h-8 w-8 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Package size={15} className="text-accent" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-text">{item.label}</p>
                <p className="text-xs text-muted mt-0.5 truncate">
                  {item.equipmentKind} · {WEIGHT_CLASSES.find(w => w.value === item.weightClass)?.label}
                  {(item.make || item.model) && ` · ${[item.make, item.model].filter(Boolean).join(' ')}`}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDeleteTarget(item.id)}
                className="!text-muted hover:!text-danger hover:!bg-transparent flex-shrink-0 mt-0.5"
                aria-label="Delete equipment"
              >
                <Trash2 size={15} />
              </Button>
            </div>
            </AnimateIn>
          ))}
        </div>
      )}

      {/* Delete equipment confirmation */}
      <ConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Remove this equipment?"
        message="This saved equipment profile will be permanently removed."
        confirmLabel="Remove"
        danger
        loading={deleteEquipment.isPending}
      />

      {/* Add equipment modal */}
      <Modal open={addOpen} onClose={closeModal} title="Add saved equipment" maxWidth="max-w-lg" hideCloseButton>
        <div className="flex flex-col gap-4">

          {/* Step indicator */}
          <div className="flex divide-x divide-line border-b border-line -mt-1">
            {STEPS.map((s, i) => (
              <div
                key={s}
                className={`flex-1 py-2 text-center text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  i < step ? 'bg-accent/10 text-accent' : i === step ? 'bg-accent text-black' : 'text-muted'
                }`}
              >
                {i < step ? <Check size={11} className="mx-auto" /> : s}
              </div>
            ))}
          </div>

          <div className="rounded-2xl p-4 bg-surface">
            <div key={stepName} className="step-enter flex flex-col gap-4">

              {/* ── Type ──────────────────────────────────────────────────── */}
              {stepName === 'Type' && (
                <>
                  <div>
                    <p className={labelClass}>Equipment type</p>
                    <div role="radiogroup" aria-label="Equipment type" className="grid grid-cols-3 gap-2">
                      {EQUIPMENT_KINDS.map((kind) => (
                        <button
                          key={kind}
                          type="button"
                          role="radio"
                          aria-checked={draft.equipmentKind === kind}
                          onClick={() => setDraft(d => ({ ...d, equipmentKind: kind }))}
                          className={`rounded-xl p-3 text-center transition-colors ${
                            draft.equipmentKind === kind ? 'bg-accent' : 'bg-bg hover:bg-surface-raised'
                          }`}
                        >
                          <p className={`text-[11px] font-bold leading-tight ${
                            draft.equipmentKind === kind ? 'text-black' : 'text-text'
                          }`}>
                            {kind}
                          </p>
                        </button>
                      ))}
                    </div>
                    {stepAttempted && !draft.equipmentKind && (
                      <p className="text-xs text-danger mt-1.5">Please select an equipment type.</p>
                    )}
                  </div>

                  {draft.equipmentKind === 'Custom' && (
                    <div className="element-enter">
                      <label className={labelClass}>Describe your equipment</label>
                      <input
                        className={inputClass}
                        placeholder="e.g. Crane, Forklift, Generator…"
                        value={draft.equipmentDesc}
                        onChange={(e) => setDraft(d => ({ ...d, equipmentDesc: e.target.value }))}
                      />
                    </div>
                  )}
                </>
              )}

              {/* ── Weight ────────────────────────────────────────────────── */}
              {stepName === 'Weight' && (
                <div>
                  <p className={labelClass}>Weight class</p>
                  <div role="radiogroup" aria-label="Weight class" className="grid grid-cols-2 gap-2">
                    {WEIGHT_CLASSES.map((wc) => (
                      <button
                        key={wc.value}
                        type="button"
                        role="radio"
                        aria-checked={draft.weightClass === wc.value}
                        onClick={() => setDraft(d => ({ ...d, weightClass: wc.value }))}
                        className={`rounded-xl p-3 text-left transition-colors ${
                          draft.weightClass === wc.value ? 'bg-accent' : 'bg-bg hover:bg-surface-raised'
                        }`}
                      >
                        <p className={`text-sm font-bold ${draft.weightClass === wc.value ? 'text-black' : 'text-text'}`}>
                          {wc.label}
                        </p>
                        <p className={`text-xs mt-0.5 ${draft.weightClass === wc.value ? 'text-black/60' : 'text-muted'}`}>
                          {wc.range}
                        </p>
                      </button>
                    ))}
                  </div>
                  {stepAttempted && !draft.weightClass && (
                    <p className="text-xs text-danger mt-1.5">Please select a weight class.</p>
                  )}
                </div>
              )}

              {/* ── Details ───────────────────────────────────────────────── */}
              {stepName === 'Details' && (
                <>
                  <div>
                    <label className={labelClass}>Label (e.g. "My CAT 320D Excavator")</label>
                    <input
                      type="text"
                      value={draft.label}
                      onChange={(e) => setDraft(d => ({ ...d, label: e.target.value }))}
                      placeholder="Give this equipment a name"
                      className={inputClass}
                    />
                    {stepAttempted && !draft.label.trim() && (
                      <p className="text-xs text-danger mt-1.5">Required</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Make</label>
                      <input
                        className={inputClass}
                        placeholder="e.g. Caterpillar"
                        value={draft.make}
                        onChange={(e) => setDraft(d => ({ ...d, make: e.target.value }))}
                      />
                      {stepAttempted && !draft.make.trim() && (
                        <p className="text-xs text-danger mt-1.5">Required</p>
                      )}
                    </div>
                    <div>
                      <label className={labelClass}>Model</label>
                      <input
                        className={inputClass}
                        placeholder="e.g. 320D"
                        value={draft.model}
                        onChange={(e) => setDraft(d => ({ ...d, model: e.target.value }))}
                      />
                      {stepAttempted && !draft.model.trim() && (
                        <p className="text-xs text-danger mt-1.5">Required</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Estimated weight (tons, optional)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      className={inputClass}
                      placeholder="e.g. 12.5"
                      value={draft.estimatedWeight}
                      onChange={(e) => setDraft(d => ({ ...d, estimatedWeight: e.target.value }))}
                    />
                  </div>

                  <PhotoUpload
                    label="Photo"
                    photoUrl={draft.photoUrl}
                    onChange={(url) => setDraft(d => ({ ...d, photoUrl: url }))}
                    upload={uploadEquipmentPhoto}
                  />
                </>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-2">
            <Button
              variant="secondary"
              onClick={step === 0 ? closeModal : prev}
              className="!text-sm normal-case !tracking-normal rounded-xl"
            >
              {step > 0 && <ChevronLeft size={15} />}
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>

            {!isLastStep ? (
              <Button
                onClick={next}
                className={`!text-sm normal-case !tracking-normal rounded-xl ${!canProceed() ? '!opacity-30' : ''}`}
              >
                Continue
                <ArrowRight size={15} />
              </Button>
            ) : (
              <Button
                onClick={handleAdd}
                loading={addEquipment.isPending}
                loadingText="Saving…"
                className={`!text-sm normal-case !tracking-normal rounded-xl ${!canSave ? '!opacity-30' : ''}`}
              >
                <Check size={15} />
                Save Equipment
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
